import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, Briefcase, Search, LayoutGrid, List, Mail, Phone, Star, Building2, Trash2, Pencil, KeyRound } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

const formatRoleName = (role) => {
  const roleNames = { vendor: 'Vendor', labour: 'Labour' };
  return roleNames[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Unknown');
};

export default function VendorsLabourList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'vendor', phone: '' });
  // iter-308: Edit / Reset Password modal state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editData, setEditData] = useState({ name: '', phone: '', password: '' });
  const [editSaving, setEditSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => ['vendor', 'labour'].includes(u.role) && u.status !== 'deleted'));
    } catch (error) {
      toast.error('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVendor = async (e, user) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${formatRoleName(user.role)} "${user.name}"?\n\nThey will be hidden from all lists. Their past payments/work history is preserved.`)) return;
    try {
      await axios.delete(`${API}/vendors/${user.id}`);
      toast.success(`${formatRoleName(user.role)} deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // iter-308: open Edit / Reset Password modal
  const openEditDialog = (e, user) => {
    e.stopPropagation();
    setEditTarget(user);
    setEditData({ name: user.name || '', phone: user.phone || '', password: '' });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    if (editData.password && editData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      setEditSaving(true);
      const payload = { name: editData.name, phone: editData.phone };
      if (editData.password) payload.password = editData.password;
      await axios.put(`${API}/users/${editTarget.id}`, payload);
      // If password was changed, also revoke active sessions so the vendor
      // is forced to re-login with the new credential.
      if (editData.password) {
        try { await axios.post(`${API}/users/${editTarget.id}/logout-everywhere`); } catch {}
        toast.success(`Password reset for ${editTarget.name}. All their sessions have been revoked.`);
      } else {
        toast.success(`${formatRoleName(editTarget.role)} "${editTarget.name}" updated`);
      }
      setEditDialogOpen(false);
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    // iter-304b: confirmation dialog with full vendor/labour summary
    const summary = `Create ${formatRoleName(formData.role)}?\n\nName: ${formData.name}` +
      (formData.email ? `\nEmail: ${formData.email}` : '\nEmail: (auto-generated)') +
      (formData.phone ? `\nPhone: ${formData.phone}` : '') +
      `\n\nThis user will be added to the directory immediately.`;
    if (!window.confirm(summary)) return;
    try {
      const submitData = { ...formData };
      // Remove empty email so backend auto-generates a placeholder address
      if (!submitData.email) { delete submitData.email; }
      if (!submitData.password) {
        submitData.password = `Bc${Date.now().toString(36)}!`;
      }
      const resp = await axios.post(`${API}/auth/register`, submitData);
      const createdUser = resp.data.user;
      // Update phone if provided
      if (submitData.phone && createdUser?.id) {
        try {
          await axios.put(`${API}/users/${createdUser.id}`, { phone: submitData.phone });
        } catch {}
      }
      toast.success(`${formatRoleName(formData.role)} "${createdUser?.name || formData.name}" added to directory`, { duration: 5000 });
      setDialogOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'vendor', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, roleFilter]);

  const vendorCount = users.filter(u => u.role === 'vendor').length;
  const labourCount = users.filter(u => u.role === 'labour').length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="vendors-labours-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-stone-900">Vendors & Labour</h1>
            <p className="text-stone-500 mt-1">{vendorCount} Vendors, {labourCount} Labour</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
              <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-48" data-testid="vendors-search" />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="vendor">Vendors</SelectItem>
                <SelectItem value="labour">Labour</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex bg-stone-100 rounded-lg p-0.5">
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}><LayoutGrid size={16} /></button>
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}><List size={16} /></button>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2" data-testid="add-vendor-btn"><Plus size={16} /> Add</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Vendor / Labour</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div><Label>Name *</Label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="vendor-name-input" /></div>
                  <div><Label>Email <span className="text-stone-400 text-xs">(optional for Labour)</span></Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Auto-generated if left empty" data-testid="vendor-email-input" /></div>
                  <div><Label>Password <span className="text-stone-400 text-xs">(optional for Labour)</span></Label><Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder="Auto-generated if left empty" data-testid="vendor-password-input" /></div>
                  <div><Label>Phone / Mobile <span className="text-stone-400 text-xs">(optional)</span></Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Mobile number" data-testid="vendor-phone-input" /></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vendor">Vendor</SelectItem>
                        <SelectItem value="labour">Labour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="btn-primary">Create</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900" /></div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-16 text-center">
            <Briefcase size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No vendors or labour found</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="vendors-list">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Rating</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-600 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-stone-50 cursor-pointer transition-colors" onClick={() => navigate(`/vendors/${user.id}`)}>
                    <td className="px-4 py-3"><span className="font-medium text-stone-900">{user.name}</span></td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-xs ${user.role === 'vendor' ? 'border-orange-200 text-orange-700 bg-orange-50' : 'border-blue-200 text-blue-700 bg-blue-50'}`}>
                        {formatRoleName(user.role)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.vendor_category || user.category || '-'}</td>
                    <td className="px-4 py-3">
                      {user.performance_score ? (
                        <div className="flex items-center gap-1">
                          <Star size={12} className="text-amber-500 fill-amber-500" />
                          <span className="text-sm font-medium">{user.performance_score.toFixed(1)}</span>
                        </div>
                      ) : <span className="text-sm text-stone-400">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <button
                          onClick={(e) => openEditDialog(e, user)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-colors"
                          title={`Edit / Reset password — ${user.name}`}
                          data-testid={`edit-vendor-${user.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteVendor(e, user)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                          title={`Delete ${formatRoleName(user.role)}`}
                          data-testid={`delete-vendor-${user.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vendors-grid">
            {filteredUsers.map(user => (
              <div key={user.id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow cursor-pointer relative group" onClick={() => navigate(`/vendors/${user.id}`)}>
                <div className="absolute top-3 right-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => openEditDialog(e, user)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                    title={`Edit / Reset password — ${user.name}`}
                    data-testid={`edit-vendor-card-${user.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={(e) => handleDeleteVendor(e, user)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700"
                    title={`Delete ${formatRoleName(user.role)}`}
                    data-testid={`delete-vendor-card-${user.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex items-start gap-3">
                  {user.logo_data_url ? (
                    <img
                      src={user.logo_data_url}
                      alt={user.name || 'vendor'}
                      className="w-10 h-10 rounded-full object-cover border border-stone-200 bg-white"
                      data-testid={`vendor-logo-${user.id}`}
                    />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${user.role === 'vendor' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                      {user.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-stone-900 truncate">{user.name}</h3>
                    <Badge variant="outline" className={`text-xs mt-1 ${user.role === 'vendor' ? 'border-orange-200 text-orange-700' : 'border-blue-200 text-blue-700'}`}>
                      {formatRoleName(user.role)}
                    </Badge>
                  </div>
                  {user.performance_score && (
                    <div className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      <Star size={12} className="fill-amber-500" />
                      <span className="text-xs font-medium">{user.performance_score.toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 space-y-1.5">
                  {user.email && <div className="flex items-center gap-2 text-sm text-stone-500"><Mail size={13} /><span className="truncate">{user.email}</span></div>}
                  {user.phone && <div className="flex items-center gap-2 text-sm text-stone-500"><Phone size={13} />{user.phone}</div>}
                  {(user.vendor_category || user.category) && <div className="flex items-center gap-2 text-sm text-stone-500"><Building2 size={13} />{user.vendor_category || user.category}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* iter-308: Edit / Reset Password dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(o) => { if (!o) { setEditDialogOpen(false); setEditTarget(null); } }}>
          <DialogContent className="sm:max-w-md" data-testid="vendor-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil size={16} className="text-blue-600" />
                Edit {editTarget ? formatRoleName(editTarget.role) : 'User'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} required data-testid="vendor-edit-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} data-testid="vendor-edit-phone" />
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                <Label className="flex items-center gap-1.5 text-amber-900 font-semibold">
                  <KeyRound size={13} />Reset Password <span className="text-stone-500 font-normal text-[11px]">(leave blank to keep current)</span>
                </Label>
                <Input
                  type="password"
                  placeholder="New password (min 8 chars)"
                  value={editData.password}
                  onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                  data-testid="vendor-edit-password"
                  autoComplete="new-password"
                />
                {editData.password && (
                  <p className="text-[11px] text-amber-800">
                    Saving will reset the password <strong>and</strong> force-sign-out this user from every device.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editSaving}>Cancel</Button>
                <Button type="submit" disabled={editSaving} data-testid="vendor-edit-save">
                  {editSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
